import axios from "axios";

export const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API_BASE = `${BACKEND_URL}/api`;

const PIN_TOKEN_KEY = "ra_pin_token";
// sessionStorage (not localStorage): cleared when the tab/app is fully closed,
// so re-opening the app always asks for the PIN again, like a lock screen.
export const getPinToken = () => sessionStorage.getItem(PIN_TOKEN_KEY) || "";
export const setPinToken = (token) => sessionStorage.setItem(PIN_TOKEN_KEY, token);
export const clearPinToken = () => sessionStorage.removeItem(PIN_TOKEN_KEY);

export const api = axios.create({
  baseURL: API_BASE,
  withCredentials: true,
});

api.interceptors.request.use((config) => {
  const t = getPinToken();
  if (t) config.headers["X-Pin-Token"] = t;
  return config;
});

// A DELETE that 404s means the record is already gone — which is exactly the
// outcome the caller asked for. Treating that as success here, once, spares
// twenty delete call sites from each needing a try/catch for a double-click or
// a stale list. Every other status, and every other verb, still rejects.
api.interceptors.response.use(
  (res) => res,
  (err) => {
    if (err.config?.method === "delete" && err.response?.status === 404) {
      return Promise.resolve({ ...err.response, data: { ok: true, already_gone: true } });
    }
    return Promise.reject(err);
  },
);

// ---- subscription-plan gating (mirrors the backend PLANS config) ----
// Admins always pass. Otherwise the user's resolved plan_info decides.
export const planAllowsModule = (user, module) => {
  if (!user) return false;
  if (user.role === "admin") return true;
  const mods = user.plan_info?.modules || [];
  return mods.includes("*") || mods.includes(module);
};

export const planHasFeature = (user, feature) => {
  if (!user) return false;
  if (user.role === "admin") return true;
  return !!user.plan_info?.features?.[feature];
};

export const fmtINR = (n) =>
  new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 2 }).format(Number(n || 0));

export const fmtDate = (d) => {
  if (!d) return "—";
  try {
    return new Date(d).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
  } catch {
    return d;
  }
};

// Local calendar date as YYYY-MM-DD. Must NOT use toISOString(), which emits
// the UTC date — for IST (+5:30) that is still "yesterday" until 05:30 every
// morning, stamping late-night entries a day early. en-CA formats as ISO.
export const todayISO = () => new Date().toLocaleDateString("en-CA");

// Sign-off printed under every shared invoice/quote/receipt. Everything comes
// from Firm Settings — a blank advocate_name falls back to the firm itself and
// blank fields drop their line, so nothing is hardcoded per-practice.
export const signOff = (firm) =>
  [
    (firm?.advocate_name || "").trim() || firm?.firm_name || "",
    (firm?.advocate_credentials || "").trim(),
    firm?.phone || "",
  ]
    .filter(Boolean)
    .join("\n");

// Plain <a href="/api/..."> links can't attach the PIN unlock header, so any
// authenticated file (PDF, uploaded document, etc.) opened that way fails
// once a PIN is set on the account. This fetches the file through the same
// axios instance (which does attach the header) and opens it as a blob URL
// instead, working exactly like a normal "open in new tab" link would.
export async function openAuthedFile(relativeUrl, opts = {}) {
  // Open the tab SYNCHRONOUSLY (still inside the click's user-activation), then
  // navigate it to the blob once fetched. Opening after the await gets blocked by
  // popup blockers (Safari/iOS especially), so the file silently never appears.
  const viewer = opts.download ? null : window.open("", "_blank");
  try {
    const res = await api.get(relativeUrl, { responseType: "blob" });
    const blobUrl = URL.createObjectURL(res.data);
    if (opts.download) {
      const a = document.createElement("a");
      a.href = blobUrl; a.download = opts.download;
      document.body.appendChild(a); a.click(); a.remove();
    } else if (viewer) {
      viewer.location = blobUrl;
    } else {
      // Popup was blocked despite the sync open — fall back to same-tab nav.
      window.location.assign(blobUrl);
    }
    setTimeout(() => URL.revokeObjectURL(blobUrl), 60000);
  } catch (e) {
    if (viewer) viewer.close();
    let msg = "Could not open file.";
    const data = e?.response?.data;
    if (data instanceof Blob) {
      try {
        const text = await data.text();
        msg = JSON.parse(text)?.detail || msg;
      } catch { /* not JSON, keep default message */ }
    } else if (data?.detail) {
      msg = data.detail;
    }
    alert(msg);
  }
}
